// Omnix/client/ui/TwilioWizard.js
import { ComponentBase } from "../core/ComponentBase.js";

export class TwilioWizard extends ComponentBase {
    static styles = `
        .twilio-wizard-modal .modal-dialog { max-width: 900px; }
        .twilio-wizard-modal .modal-body {
            min-height: 500px;
            max-height: 75vh;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            padding-top: 0; /* Remove top padding so sticky header hits the edge */
        }
        .wizard-step-content { display: none; flex-grow: 1; }
        .wizard-step-content.active { display: block; }
        
        /* Sticky Search Header */
        .search-controls-wrapper {
            position: sticky;
            top: 0;
            z-index: 10;
            background-color: var(--bs-body-bg);
            padding-top: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            margin-bottom: 1rem;
        }

        .search-row {
            display: flex;
            align-items: flex-end;
            gap: 1rem;
        }
        
        .search-item-country { flex: 0 0 200px; }
        .search-item-prefix { flex: 0 0 120px; }
        .search-item-caps { flex-grow: 1; display: flex; gap: 1rem; padding-bottom: 8px; }
        .search-item-btn { flex: 0 0 120px; }

        .search-results-container {
            flex-grow: 1;
        }

        .number-list-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .number-info { font-family: monospace; font-size: 1.1rem; font-weight: 600; }
        .number-location { font-size: 0.85rem; color: var(--bs-secondary-color); }
        .number-price { font-weight: 600; color: var(--bs-primary); }
        
        .capability-icon { font-size: 1rem; margin-right: 0.5rem; color: var(--bs-secondary-color); }
        /* Specific colors for active capabilities */
        .capability-icon.sms { color: var(--bs-success); }
        .capability-icon.voice { color: var(--bs-primary); }
        .capability-icon.mms { color: var(--bs-info); }
        
        .pricing-details-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 0.5rem;
            font-size: 0.8rem;
            background-color: var(--bs-tertiary-bg);
            padding: 0.75rem;
            border-radius: var(--bs-border-radius);
        }
    `;

    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.currentStep = 1;
        this.searchResults = [];
        this.selectedNumber = null;
        
        this.countries = [
            { code: 'US', name: 'United States' },
            { code: 'CA', name: 'Canada' },
            { code: 'GB', name: 'United Kingdom' },
            { code: 'AU', name: 'Australia' },
            { code: 'DE', name: 'Germany' },
            { code: 'FR', name: 'France' },
            { code: 'IN', name: 'India' },
            { code: 'IE', name: 'Ireland' },
            { code: 'NL', name: 'Netherlands' },
            { code: 'SE', name: 'Sweden' },
            { code: 'SG', name: 'Singapore' },
            { code: 'BR', name: 'Brazil' },
            { code: 'MX', name: 'Mexico' }
        ];
        
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade twilio-wizard-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.setAttribute('tabindex', '-1');

        const countryOptions = this.countries.map(c => 
            `<option value="${c.code}">${c.name} (${c.code})</option>`
        ).join('');

        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add New Phone Number</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <!-- Step 1: Search -->
                        <div id="wizard-step-1" class="wizard-step-content">
                            <div class="search-controls-wrapper">
                                <div class="search-row">
                                    <div class="search-item-country">
                                        <label class="form-label small mb-1">Country</label>
                                        <select id="twilio-country" class="form-select form-select-sm">
                                            ${countryOptions}
                                        </select>
                                    </div>
                                    <div class="search-item-prefix">
                                        <label class="form-label small mb-1">Prefix</label>
                                        <input type="text" id="twilio-area-code" class="form-control form-control-sm" placeholder="e.g. 415">
                                    </div>
                                    <div class="search-item-caps">
                                        <div class="form-check form-check-inline align-self-end mb-1">
                                            <input class="form-check-input" type="checkbox" id="cap-sms" value="sms" checked>
                                            <label class="form-check-label small" for="cap-sms">SMS</label>
                                        </div>
                                        <div class="form-check form-check-inline align-self-end mb-1">
                                            <input class="form-check-input" type="checkbox" id="cap-voice" value="voice" checked>
                                            <label class="form-check-label small" for="cap-voice">Voice</label>
                                        </div>
                                        <div class="form-check form-check-inline align-self-end mb-1">
                                            <input class="form-check-input" type="checkbox" id="cap-mms" value="mms">
                                            <label class="form-check-label small" for="cap-mms">MMS</label>
                                        </div>
                                    </div>
                                    <div class="search-item-btn">
                                        <button class="btn btn-sm btn-secondary w-100" id="search-numbers-btn">
                                            <i class="bi bi-search me-1"></i> Search
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="search-results-container list-group list-group-flush">
                                <div class="d-flex flex-column justify-content-center align-items-center py-5 text-muted">
                                    <i class="bi bi-telephone-plus fs-1 mb-2"></i>
                                    <p>Select criteria above and click Search.</p>
                                </div>
                            </div>
                        </div>

                        <!-- Step 2: Confirm Purchase -->
                        <div id="wizard-step-2" class="wizard-step-content">
                            <h6>Step 2: Confirm and Purchase</h6>
                            <div class="alert alert-info">
                                You are about to purchase: <strong id="confirm-number"></strong>
                                <div id="confirm-price-info" class="mt-2"></div>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Friendly Name</label>
                                <input type="text" id="twilio-friendly-name" class="form-control" placeholder="e.g., Main Support Line">
                            </div>
                            <h6 class="mt-4">Routing</h6>
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <label for="twilio-sms-workflow" class="form-label">Inbound SMS Workflow</label>
                                    <select id="twilio-sms-workflow" class="form-select">
                                        <option value="">Default (Direct to Inbox)</option>
                                    </select>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label for="twilio-voice-workflow" class="form-label">Inbound Voice Workflow</label>
                                    <select id="twilio-voice-workflow" class="form-select">
                                        <option value="">Default (Direct to Inbox)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" id="wizard-back-btn" style="display: none;">Back</button>
                        <button type="button" class="btn btn-primary" id="wizard-purchase-btn" style="display: none;">Purchase Number</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.attachEventListeners();
        await super.initialize();
    }

    show() {
        this.reset();
        this.modalInstance.show();
    }

    reset() {
        this.currentStep = 1;
        this.searchResults = [];
        this.selectedNumber = null;
        this.container.querySelector('#twilio-area-code').value = '';
        this.container.querySelector('#twilio-friendly-name').value = '';
        this.updateWizardState();
        this.container.querySelector('.search-results-container').innerHTML = `
            <div class="d-flex flex-column justify-content-center align-items-center py-5 text-muted">
                <i class="bi bi-telephone-plus fs-1 mb-2"></i>
                <p>Select criteria above and click Search.</p>
            </div>`;
    }

    async _populateWorkflows() {
        const smsSelect = this.container.querySelector('#twilio-sms-workflow');
        const voiceSelect = this.container.querySelector('#twilio-voice-workflow');
        if (!smsSelect || !voiceSelect) return;

        try {
            const [smsWorkflows, voiceWorkflows] = await Promise.all([
                this.appClient.rpcClient.call('ChannelManagerRpc.getFieldValues', {
                    fieldName: 'inboundSmsWorkflowId',
                    reference: { collection: 'workflows', valueField: '_id', displayField: 'name', filter: { tags: 'messaging' } }
                }),
                this.appClient.rpcClient.call('ChannelManagerRpc.getFieldValues', {
                    fieldName: 'inboundVoiceWorkflowId',
                    reference: { collection: 'workflows', valueField: '_id', displayField: 'name', filter: { tags: 'voice' } }
                })
            ]);

            smsSelect.innerHTML = '<option value="">Default (Direct to Inbox)</option>';
            smsWorkflows.forEach(wf => {
                const option = document.createElement('option');
                option.value = wf.value;
                option.textContent = wf.label;
                smsSelect.appendChild(option);
            });

            voiceSelect.innerHTML = '<option value="">Default (Direct to Inbox)</option>';
            voiceWorkflows.forEach(wf => {
                const option = document.createElement('option');
                option.value = wf.value;
                option.textContent = wf.label;
                voiceSelect.appendChild(option);
            });

        } catch (error) {
            this.log.error("Failed to populate workflows in TwilioWizard:", error);
        }
    }

    updateWizardState() {
        this.container.querySelectorAll('.wizard-step-content').forEach(el => el.classList.remove('active'));
        this.container.querySelector(`#wizard-step-${this.currentStep}`).classList.add('active');

        const backBtn = this.container.querySelector('#wizard-back-btn');
        const purchaseBtn = this.container.querySelector('#wizard-purchase-btn');

        backBtn.style.display = this.currentStep === 2 ? 'inline-block' : 'none';
        purchaseBtn.style.display = this.currentStep === 2 ? 'inline-block' : 'none';
    }

    attachEventListeners() {
        this.container.querySelector('#search-numbers-btn').addEventListener('click', this.handleSearch.bind(this));
        this.container.querySelector('#wizard-back-btn').addEventListener('click', () => {
            this.currentStep = 1;
            this.updateWizardState();
        });
        this.container.querySelector('#wizard-purchase-btn').addEventListener('click', this.handlePurchase.bind(this));
    }

    async handleSearch() {
        const searchBtn = this.container.querySelector('#search-numbers-btn');
        searchBtn.disabled = true;
        searchBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span>`;

        const resultsContainer = this.container.querySelector('.search-results-container');
        resultsContainer.innerHTML = '';

        try {
            const capabilities = [];
            if (this.container.querySelector('#cap-sms').checked) capabilities.push('sms');
            if (this.container.querySelector('#cap-voice').checked) capabilities.push('voice');
            if (this.container.querySelector('#cap-mms').checked) capabilities.push('mms');

            const params = {
                countryCode: this.container.querySelector('#twilio-country').value,
                areaCode: this.container.querySelector('#twilio-area-code').value.trim(),
                capabilities: capabilities
            };

            this.searchResults = await this.appClient.rpcClient.call('TwilioRpc.searchNumbers', params);
            this.renderSearchResults();
        } catch (error) {
            this.appClient.notifier.error(`Search failed: ${error.message}`);
            resultsContainer.innerHTML = `<div class="d-flex justify-content-center align-items-center h-100 text-danger"><p>${error.message}</p></div>`;
        } finally {
            searchBtn.disabled = false;
            searchBtn.innerHTML = `<i class="bi bi-search me-1"></i> Search`;
        }
    }

    renderSearchResults() {
        const resultsContainer = this.container.querySelector('.search-results-container');
        if (this.searchResults.length === 0) {
            resultsContainer.innerHTML = `<div class="d-flex justify-content-center align-items-center py-5 text-muted"><p>No available numbers found.</p></div>`;
            return;
        }
        this.searchResults.forEach(num => {
            const item = document.createElement('div');
            item.className = 'list-group-item number-list-item';

            const monthlyPrice = num.pricing?.monthlyPrice ? `$${num.pricing.monthlyPrice.toFixed(2)}/mo` : 'Price N/A';
            
            let location = num.region || '';
            if (num.locality) location = `${num.locality}, ${location}`;
            if (!location) location = 'Region N/A';

            // --- FIX: Case-insensitive capability check ---
            const caps = (num.capabilities || []).map(c => c.toLowerCase());
            
            let icons = '';
            if (caps.includes('sms')) icons += `<i class="bi bi-chat-text-fill capability-icon sms" title="SMS"></i>`;
            if (caps.includes('voice')) icons += `<i class="bi bi-telephone-fill capability-icon voice" title="Voice"></i>`;
            if (caps.includes('mms')) icons += `<i class="bi bi-image-fill capability-icon mms" title="MMS"></i>`;

            const sourceBadge = num.source === 'inventory' 
                ? `<span class="badge bg-success ms-2">Instant</span>` 
                : '';

            item.innerHTML = `
                <div>
                    <div class="d-flex align-items-center">
                        <span class="number-info me-2">${num.friendlyName}</span>
                        ${sourceBadge}
                    </div>
                    <div class="number-location">${location}</div>
                    <div class="mt-1">${icons}</div>
                </div>
                <div class="text-end">
                    <div class="number-price mb-1">${monthlyPrice}</div>
                    <button class="btn btn-sm btn-outline-success">Select</button>
                </div>
            `;
            item.querySelector('button').addEventListener('click', async () => {
                this.selectedNumber = num;
                this.currentStep = 2;
                this.container.querySelector('#confirm-number').textContent = num.friendlyName;

                const priceInfoContainer = this.container.querySelector('#confirm-price-info');
                const p = num.pricing || {};
                
                priceInfoContainer.innerHTML = `
                    <div class="pricing-details-grid">
                        <div class="price-item"><div>Monthly Fee</div><strong>${p.monthlyPrice ? '$' + p.monthlyPrice.toFixed(2) : 'N/A'}</strong></div>
                        <div class="price-item"><div>Inbound SMS</div><strong>${p.inboundSmsPrice ? '$' + p.inboundSmsPrice.toFixed(4) : 'N/A'}</strong></div>
                        <div class="price-item"><div>Outbound SMS</div><strong>${p.outboundSmsPrice ? '$' + p.outboundSmsPrice.toFixed(4) : 'N/A'}</strong></div>
                        <div class="price-item"><div>Inbound Call</div><strong>${p.inboundCallPrice ? '$' + p.inboundCallPrice.toFixed(4) + '/min' : 'N/A'}</strong></div>
                    </div>
                `;

                this.container.querySelector('#twilio-friendly-name').value = num.friendlyName.replace(/\s/g, '-');
                this.updateWizardState();
                await this._populateWorkflows();
            });
            resultsContainer.appendChild(item);
        });
    }

    async handlePurchase() {
        const purchaseBtn = this.container.querySelector('#wizard-purchase-btn');
        purchaseBtn.disabled = true;
        purchaseBtn.innerHTML = `<span class="spinner-border spinner-border-sm"></span> Purchasing...`;

        try {
            const params = {
                phoneNumber: this.selectedNumber.phoneNumber,
                friendlyName: this.container.querySelector('#twilio-friendly-name').value.trim(),
                inboundSmsWorkflowId: this.container.querySelector('#twilio-sms-workflow').value || null,
                inboundVoiceWorkflowId: this.container.querySelector('#twilio-voice-workflow').value || null,
            };
            if (!params.friendlyName) throw new Error("Friendly Name is required.");

            await this.appClient.rpcClient.call('TwilioRpc.purchaseNumber', params);
            this.appClient.notifier.success(`Successfully purchased ${params.phoneNumber}!`);
            this.emit('channelSaved');
            this.modalInstance.hide();
        } catch (error) {
            this.appClient.notifier.error(`Purchase failed: ${error.message}`);
        } finally {
            purchaseBtn.disabled = false;
            purchaseBtn.textContent = 'Purchase Number';
        }
    }

    render() { return this.container; }
    destroy() { this.modalInstance?.dispose(); this.container?.remove(); super.destroy(); }
}